import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { TaskRiskPolicyDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

type TaskRiskIPMatchMode = 'exact' | 'ipv4_24' | 'ipv4_16';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid task risk policy': '任务风控参数无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useTaskRiskPolicy() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<TaskRiskPolicyDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxEffectiveAssignmentsPerTaskIP, setMaxEffectiveAssignmentsPerTaskIP] = useState(0);
  const [ipMatchMode, setIPMatchMode] = useState<TaskRiskIPMatchMode>('exact');
  const [claimRequireWithdrawalAccount, setClaimRequireWithdrawalAccount] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getTaskRiskPolicy();
      setData(result);
      setMaxEffectiveAssignmentsPerTaskIP(result.max_effective_assignments_per_task_ip);
      setIPMatchMode(result.ip_match_mode || 'exact');
      setClaimRequireWithdrawalAccount(Boolean(result.claim_require_withdrawal_account));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const result = await settingsService.updateTaskRiskPolicy({
        maxEffectiveAssignmentsPerTaskIP: Number.isFinite(maxEffectiveAssignmentsPerTaskIP)
          ? maxEffectiveAssignmentsPerTaskIP
          : 0,
        ipMatchMode,
        claimRequireWithdrawalAccount,
      });
      setData(result);
      setMaxEffectiveAssignmentsPerTaskIP(result.max_effective_assignments_per_task_ip);
      setIPMatchMode(result.ip_match_mode || 'exact');
      setClaimRequireWithdrawalAccount(Boolean(result.claim_require_withdrawal_account));
      addToast('success', '任务风控配置已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [addToast, claimRequireWithdrawalAccount, ipMatchMode, maxEffectiveAssignmentsPerTaskIP]);

  return {
    data,
    loading,
    saving,
    error,
    maxEffectiveAssignmentsPerTaskIP,
    setMaxEffectiveAssignmentsPerTaskIP,
    ipMatchMode,
    setIPMatchMode,
    claimRequireWithdrawalAccount,
    setClaimRequireWithdrawalAccount,
    save,
    reload: load,
  };
}
